import _ from 'lodash'

import { mapState, mapGetters } from 'vuex'

import { socket } from '@/aiplatform_socketio'

import {
  dict_from_frozendict,
  extended_dict_forEach,
  extended_dict_getValue,
  project_auth_groups_enum,
} from '@/utils.js'

import { generic_subscription_update } from '@/api/subscriptions.js'
import {
  encrypt_string,
  decrypt_string,
  platform_encrypt_string,
  platform_decrypt_string,
} from '../utils'

import stringify from 'json-stable-stringify'

// *****************************************************************************

class subscription_object_collection_type {
  constructor() {
    // the Vuex store
    this.store = undefined
    // token is a unique id per component
    // key is the subscription path
    this.token_to_keys = {}
    this.key_to_tokens = {}
    // Adding / removing subscriptions is deferred and
    // to_remove and to_add are the buffers
    this.to_add = []
    this.to_remove = []
    // Deferring of actual subscriptions is done by throttling
    // either from 50 ms for add and 5s for remove to prevent jitter
    // chat with the server
    this.throttled_do_add_subscription = _.throttle(
      () => this.do_add_subscription(),
      50,
      { leading: false, trailing: true }
    )
    this.throttled_do_remove_subscription = _.throttle(
      () => this.do_remove_subscription(),
      5000,
      { leading: false, trailing: true }
    )
  }

  set_store(store) {
    this.store = store
  }

  do_add_subscription() {
    const to_add = this.to_add
    this.to_add = []
    let current_session_id = socket.id

    let subscriptions_to_add = []

    for (const [origin_session_id, token, keys] of to_add) {
      console.assert(keys.length !== 0)
      if (current_session_id != origin_session_id) {
        continue
      }
      let current_keys = _.get(this.token_to_keys, token, [])
      current_keys.push(...keys)
      this.token_to_keys[token] = current_keys

      for (const key of keys) {
        let current_tokens = _.get(this.key_to_tokens, key)
        if (_.isUndefined(current_tokens)) {
          subscriptions_to_add.push(key)
          current_tokens = []
        }
        current_tokens.push(token)
        this.key_to_tokens[key] = current_tokens
      }
    }

    if (subscriptions_to_add.length !== 0) {
      generic_subscription_update(subscriptions_to_add, [])
    }
  }

  do_remove_subscription() {
    // We need to call add_subscription first because, because of throttling
    // there could be a case where remove is called before the subscription is
    // actually added, which is forbidden...
    this.do_add_subscription()

    const to_remove = this.to_remove
    this.to_remove = []
    let current_session_id = socket.id

    let subscriptions_to_remove = []

    for (const [origin_session_id, token, keys] of to_remove) {
      console.assert(keys.length !== 0)
      if (current_session_id != origin_session_id) {
        continue
      }

      let current_keys = _.get(this.token_to_keys, token, [])
      for (const key of keys) {
        _.pullAt(current_keys, [_.indexOf(current_keys, key)])
      }
      if (current_keys.length === 0) {
        delete this.token_to_keys[token]
      }

      for (const key of keys) {
        let current_tokens = this.key_to_tokens[key]
        _.pullAt(current_tokens, [_.indexOf(current_tokens, token)])

        if (current_tokens.length === 0) {
          subscriptions_to_remove.push(key)
          delete this.key_to_tokens[key]
        }
      }
    }

    if (subscriptions_to_remove.length !== 0) {
      // We cannot directly remove from the store, we need to use
      // a commit transaction for that, which will defer removal
      this.store.commit('cleanup_subscriptions', subscriptions_to_remove)
      generic_subscription_update([], subscriptions_to_remove)
    }
  }

  restart_subscriptions() {
    const to_add = _.keys(this.key_to_tokens)
    generic_subscription_update(to_add, [])
  }

  remove_add_subscriptions(token, keys_to_remove, keys_to_add) {
    // We need to check the session id in do_remove_subscription
    // and not do_add_subscription because do_remove_subscription
    // is delayed (so that off/on subscriptions do not cause jitter)
    let origin_session_id = socket.id
    if (keys_to_remove.length !== 0) {
      this.to_remove.push([origin_session_id, token, keys_to_remove])
      this.throttled_do_remove_subscription()
    }
    if (keys_to_add.length !== 0) {
      this.to_add.push([origin_session_id, token, keys_to_add])
      this.throttled_do_add_subscription()
    }
  }
}

let subscription_object_collection = new subscription_object_collection_type()

export { subscription_object_collection }

// *****************************************************************************

class subscription_handler_type {
  constructor(component) {
    this.component = component
    this.reservations_to_keys = {}
  }
  release() {
    let component = this.component
    let to_release = _.uniq(_.concat(..._.values(this.reservations_to_keys)))
    // We need to clear all the reservations because release
    // may be called multiple times, notably when Vue calls
    // beforeDestroy multiple times...
    this.reservations_to_keys = {}

    // console.log(to_release)

    subscription_object_collection.remove_add_subscriptions(
      this.component.uid,
      to_release,
      []
    )
  }

  reserve(name, new_keys) {
    const old_keys = this.reservations_to_keys[name]
    if (_.isEqual(old_keys, new_keys)) {
      return
    }

    const old_allkeys = _.uniq(_.concat(..._.values(this.reservations_to_keys)))
    this.reservations_to_keys[name] = new_keys
    const new_allkeys = _.uniq(_.concat(..._.values(this.reservations_to_keys)))

    const to_remove = _.difference(old_allkeys, new_allkeys)
    const to_add = _.difference(new_allkeys, old_allkeys)
    // console.log(to_remove, to_add)
    if (to_remove.length !== 0 || to_add.length !== 0) {
      subscription_object_collection.remove_add_subscriptions(
        this.component.uid,
        to_remove,
        to_add
      )
    }
  }
}
// *****************************************************************************

// Used by coercion_date_formats, filter_search_list_element, unroll_element

let tagid_mixin = {
  data() {
    const uid = _.uniqueId('tokensub')
    return {
      uid,
    }
  },
  methods: {
    $id(tid) {
      return 'tid-' + this.uid + '-' + tid
    },
  },
}

export { tagid_mixin }

// ************************************************************************* //

let platform_encryption_mixin = {
  methods: {
    platform_encrypt_string(plaintext, encoding = 'utf8') {
      return platform_encrypt_string(plaintext, encoding)
    },
    platform_decrypt_string(plaintext, encoding = 'utf8') {
      return platform_decrypt_string(plaintext, encoding)
    },
  },
}

export { platform_encryption_mixin }

// ************************************************************************* //

let client_encryption_mixin = {
  computed: {
    ...mapGetters(['client_encryption_key']),
  },
  methods: {
    client_encrypt_string(plaintext, encoding = 'utf8') {
      const client_key = platform_decrypt_string(
        this.client_encryption_key,
        'binary'
      )
      const message = encrypt_string(plaintext, client_key, encoding)
      return message
    },
    client_decrypt_string(message, encoding = 'utf8') {
      const client_key = platform_decrypt_string(
        this.client_encryption_key,
        'binary'
      )
      const plaintext = decrypt_string(message, client_key, encoding)
      return plaintext
    },
    default_client_decrypt_string(
      message,
      { encoding = 'utf8', default_value = null } = {}
    ) {
      if (message === default_value) {
        return message
      }
      const plaintext = this.client_decrypt_string(message, encoding)
      return plaintext
    },
  },
}

export { client_encryption_mixin }

// ************************************************************************* //

let project_mixin = {
  computed: {
    project_id() {
      const route = this.$route.path
      const regexp = new RegExp('/projects/(\\d*)/')

      let res = Number((route.match(regexp) || {})[1]) || -1

      return res
    },

    cur_project() {
      let res =
        (this.automl_projects_undeleted_object.projects || {})[
          this.project_id
        ] || {}
      return res
    },
  },
}

export { project_mixin }

// ************************************************************************* //

/*TODO: use the links below to generate properties so that we do
not have to repeat the name as both the computed function name
and as the subscription local component token
https://forum.vuejs.org/t/generating-computed-properties-on-the-fly/14833/5
https://jsfiddle.net/Linusborg/3p4kpz1t/
*/

function collection_global_subscription_string_maker(collection) {
  const key = `/collection/${collection}/global`
  return key
}

function collection_per_project_subscription_string_maker(
  collection,
  project_id
) {
  const key = `/collection/${collection}/project/${project_id}`
  return key
}

function brick_progress_subscription_string_maker(project_id, brick_id) {
  const key = `/project/${project_id}/brick/${brick_id}/progress`
  return key
}

function poid_status_subscription_string_maker(project_id, poid) {
  const key = `/project/${project_id}/pipe/${poid}/status`
  return key
}

function poid_tracking_info_subscription_string_maker(project_id, poid) {
  const key = `/project/${project_id}/pipe/${poid}/tracking_info`
  return key
}

function poid_data_subscription_string_maker(project_id, poid) {
  const key = `/project/${project_id}/pipe/${poid}/data`
  return key
}

function poid_columnstore_metadata_subscription_string_maker(
  project_id,
  poid,
  start,
  stop
) {
  const key = `/project/${project_id}/pipe/${poid}/columnstore_metadata/${start}/${stop}`
  return key
}

export { poid_columnstore_metadata_subscription_string_maker }

const category_to_subscription_string_maker = {
  brick_progress: brick_progress_subscription_string_maker,
  poid_status: poid_status_subscription_string_maker,
  poid_tracking_info: poid_tracking_info_subscription_string_maker,
  poid_data: poid_data_subscription_string_maker,
}

let subscription_mixin = {
  data() {
    const uid = _.uniqueId('tokensub')
    return {
      uid,
      subscription_handler: new subscription_handler_type(this),
    }
  },
  computed: {
    ...mapState(['subscribed_objects']),
  },

  beforeDestroy() {
    this.subscription_handler.release()
  },
  methods: {
    $id(tid) {
      return 'tid-' + this.uid + '-' + tid
    },
    reserve_keys(name, keys) {
      this.subscription_handler.reserve(name, keys)
    },
    do_fetch_result_with_key(name, key) {
      this.subscription_handler.reserve(name, [key])
      let res = this.subscribed_objects[key]
      return res
    },
    fetch_one_collection_for_project_id(collection) {
      // const key = `/collection/${collection}/project/${this.project_id}`
      const key = collection_per_project_subscription_string_maker(
        collection,
        this.project_id
      )
      // N.B PL : Means we can't register for a collection of a project and then
      // for all these collections -> Is it ok ?
      this.do_fetch_result_with_key(collection, key)
    },
    fetch_one_collection(collection) {
      // const key = `/collection/${collection}/global`
      const key = collection_global_subscription_string_maker(collection)
      // N.B PL : We use collection as name so that if we ask the same collection
      // twice, it does nothing -> Is this ok ?
      this.do_fetch_result_with_key(collection, key)
    },
    fetch_collections(collections) {
      for (const collection of collections) {
        this.fetch_one_collection(collection)
      }
    },
    unfetch_collections(collections) {
      for (const collection of collections) {
        this.subscription_handler.reserve(collection, undefined)
      }
    },
    fetch_object_id_with_key(name, project_id, object_id, key) {
      if (
        !_.isNumber(project_id) ||
        project_id === -1 ||
        !_.isNumber(object_id) ||
        object_id === -1
      ) {
        return undefined
      }
      const res = this.do_fetch_result_with_key(name, key)
      return res
    },
    fetch_brick_progress(name, brick_id) {
      // const key = `/project/${this.project_id}/brick/${brick_id}/progress`
      const key = brick_progress_subscription_string_maker(
        this.project_id,
        brick_id
      )
      // let res = this.fetch_object_id_with_key(
      //   name,
      //   this.project_id,
      //   brick_id,
      //   key
      // )
      let res = this.fetch_object_id_with_key(
        name,
        this.project_id,
        brick_id,
        key
      )
      // console.log(res)
      return (res || [])[0]
    },
    fetch_brick_computation_time(name, brick_id) {
      // const key = `/project/${this.project_id}/brick/${brick_id}/progress`
      const key = brick_progress_subscription_string_maker(
        this.project_id,
        brick_id
      )
      // let res = this.fetch_object_id_with_key(
      //   name,
      //   this.project_id,
      //   brick_id,
      //   key
      // )
      let res = this.fetch_object_id_with_key(
        name,
        this.project_id,
        brick_id,
        key
      )
      // console.log(res)
      return (res || [])[1]
    },
    fetch_poid_status(name, poid) {
      // N.B PL : We could also let the project_id be in data
      // and if undefined, just console.log a warning and return ?
      // const key = `/project/${this.project_id}/pipe/${poid}/status`
      const key = poid_status_subscription_string_maker(this.project_id, poid)
      let res = this.fetch_object_id_with_key(name, this.project_id, poid, key)
      return res
    },
    fetch_poid_tracking_info(name, poid) {
      // const key = `/project/${this.project_id}/pipe/${poid}/tracking_info`
      const key = poid_tracking_info_subscription_string_maker(
        this.project_id,
        poid
      )
      let res = this.fetch_object_id_with_key(name, this.project_id, poid, key)
      return res
    },
    fetch_poid_data(name, poid) {
      // const key = `/project/${this.project_id}/pipe/${poid}/data`
      const key = poid_data_subscription_string_maker(this.project_id, poid)
      let res = this.fetch_object_id_with_key(name, this.project_id, poid, key)
      return res
    },
    fetch_many(name, category_and_arguments_list) {
      const project_id = this.project_id
      let keys = []
      let res = []
      for (const [category, ...args] of category_and_arguments_list) {
        const key = category_to_subscription_string_maker[category](
          project_id,
          ...args
        )
        keys.push(key)
        res.push(this.subscribed_objects[key])
      }
      this.reserve_keys(name, keys)
      return res
    },
  },
}

export { subscription_mixin }

// ************************************************************************* //

let brick_mixin = {
  computed: {
    bricks_undeleted() {
      return this.automl_bricks_undeleted_object_per_project(this.project_id)
    },
    tables_undeleted() {
      let res = this.automl_tables_undeleted_object_per_project(this.project_id)
      return res
    },
    table_options() {
      // Used for tables with single parent
      let options = [{ value: -1, text: this.$t('choose_a_table') }]
      let self = this
      _.forEach(this.tables_undeleted['lists'], function(x) {
        // Prevent self referencing
        if (x !== self.brick_id) {
          options.push({
            value: x,
            text: self.bricks_undeleted['bricks'][x].name,
          })
        }
      })
      return options
    },
    cur_project() {
      let res =
        (this.automl_projects_undeleted_object.projects || {})[
          this.project_id
        ] || {}
      return res
    },
    cur_brick() {
      let res = (this.tables_undeleted['tables'] || {})[this.brick_id] || {}
      return res
    },
    changes_disabled() {
      let res = false
      if (this.cur_project.is_in_production) {
        res = true
      } else {
        let corresponding_project_rights = _.filter(
          this.automl_project_rights_undeleted_object.project_rights,
          {
            user_id: this.authenticated_user_id,
            project_id: this.project_id,
          }
        )

        if (
          !_.isEmpty(corresponding_project_rights) &&
          corresponding_project_rights[0].project_group_id ==
            project_auth_groups_enum.reader
        ) {
          res = true
        }
      }

      return res
    },
    is_valid_brick() {
      return !_.isEmpty(this.cur_brick)
    },

    poids_instantiated() {
      return _.map(this.metavariables_dicts_poids || [], poid =>
        this.fetch_poid_status('poid_instantiated' + poid.toString(), poid)
      )
    },
    has_unsaved_changes() {
      return !_.isMatchWith(this.cur_brick, this.adapted_data, _.isEqual)
    },
    ...mapGetters([
      'automl_bricks_undeleted_object_per_project',
      'automl_tables_undeleted_object_per_project',
      'automl_projects_undeleted_object',
      'automl_project_rights_undeleted_object',
      'authenticated_user_id',
    ]),
  },
  watch: {
    has_unsaved_changes: {
      immediate: true,
      handler(nv, ov) {
        this.$emit('update_has_unsaved_changes', nv)
      },
    },
  },
  beforeMount() {
    this.fetch_collections(['bricks', 'tables'])
  },
}

export { brick_mixin }

// ************************************************************************* //

let metavariables_tracking_info_mixin = {
  computed: {
    available_metavariables_dicts() {
      let res = {}
      let dict = this.cur_brick.last_poids_build_cache || {}
      extended_dict_forEach(dict, (metavars, poid) => {
        res[parseInt(poid)] = dict_from_frozendict(metavars)
      })
      return res
    },
    failed_metavariables_dicts() {
      let res = []
      let dict = this.cur_brick.compute_failure_reasons || {}
      extended_dict_forEach(dict, (metavars, poid) => {
        res.push(dict_from_frozendict(metavars))
      })
      return res
    },

    tracking_infos() {
      const category_and_arguments_list = _.map(
        this.metavariables_dicts_poids,
        poid => ['poid_tracking_info', poid]
      )

      const tracking_infos = this.fetch_many(
        'tracking_info_for_metavariables_dicts',
        category_and_arguments_list
      )
      const res = _.map(tracking_infos, ti => ti || {}) || []
      return res
    },
    // tracking_infos() {
    //   let res =
    //     _.map(
    //       this.metavariables_dicts_poids,
    //       poid =>
    //         this.fetch_poid_tracking_info(
    //           `tracking_info_for_metavariables_dict_${poid}`,
    //           poid
    //         ) || {}
    //     ) || []
    //   return res
    // },
  },
  methods: {
    stringified_dict(poid) {
      // return JSON.stringify(this.available_metavariables_dicts[poid] || '')
      return stringify(this.available_metavariables_dicts[poid] || '')
    },
  },
}

export { metavariables_tracking_info_mixin }

// ************************************************************************* //

let brick_generic_modelgenerator_tracking_info_mixin = {
  computed: {
    metavariables_tracking_info() {
      let res =
        this.fetch_poid_tracking_info(
          'metavariables_tracking_info',
          this.metavariables_poid
        ) || {}
      // console.log(
      //   'this.metavariables_poid',
      //   this.uid,
      //   this.project_id,
      //   this.metavariables_poid
      // )
      // console.log('metavariables_tracking_info', JSON.stringify(res))

      // console.log(this.project_id, this.brick_id, this.uid)
      // console.log('metavar tracking infos', res)
      // console.log('metavar tracking infos.search_poid', res.search_poid)
      return res
    },
    model_tracking_info() {
      // console.log('model_tracking_info')
      // let res =
      //   this.fetch_poid_tracking_info(
      //     `model_tracking_info_${this.metavariables_tracking_info.search_poid}`,
      //     this.metavariables_tracking_info.search_poid
      //   ) || {}
      let res =
        this.fetch_poid_tracking_info(
          'model_tracking_info',
          this.metavariables_tracking_info.search_poid
        ) || {}
      // console.log(
      //   'this.metavariables_tracking_info',
      //   this.metavariables_tracking_info
      // )
      // console.log('model tracking infos', res)
      return res
    },

    // metavariables_tracking_info() {
    //   let res =
    //     this.fetch_poid_tracking_info(
    //       `metavariables_tracking_info_${this.metavariables_poid}`,
    //       this.metavariables_poid
    //     ) || {}
    //   console.log(this.project_id, this.brick_id, this.uid)
    //   console.log('metavar tracking infos', res)
    //   console.log('metavar tracking infos.search_poid', res.search_poid)
    //   return res
    // },
    // model_tracking_info() {
    //   console.log('model_tracking_info')
    //   // let res =
    //   //   this.fetch_poid_tracking_info(
    //   //     `model_tracking_info_${this.metavariables_tracking_info.search_poid}`,
    //   //     this.metavariables_tracking_info.search_poid
    //   //   ) || {}
    //   let res =
    //     this.fetch_poid_tracking_info(
    //       `model_tracking_info_${this.metavariables_poid}`,
    //       this.metavariables_tracking_info.search_poid
    //     ) || {}
    //   console.log('model tracking infos', res)
    //   return res
    // },

    caruana_search_tracking_info() {
      let res = this.fetch_poid_tracking_info(
        'caruana_search_tracking_info',
        this.model_tracking_info.caruana_search
      )
      // console.log('caruana_search_tracking_info', JSON.stringify(res))

      return res
    },

    // caruana_model_searches() {
    //   const model_searches = (this.caruana_search_tracking_info || {})
    //     .model_searches
    //   let res = {}
    //   for (const [name, poid] of _.entries(model_searches)) {
    //     res[name] = this.fetch_poid_tracking_info(
    //       `caruana_model_searches_${name}_${poid}`,
    //       poid
    //     )
    //   }
    //   return res
    // },
    caruana_model_searches() {
      const model_searches = (this.caruana_search_tracking_info || {})
        .model_searches

      let category_and_arguments_list = _.map(model_searches, poid => [
        'poid_tracking_info',
        poid,
      ])
      let fetched = this.fetch_many(
        'caruana_model_searches',
        category_and_arguments_list
      )
      let res = _.fromPairs(_.zip(_.keys(model_searches), fetched))

      return res
    },

    models() {
      let res = this.metavariables_tracking_info.automatic_model_selection || []
      return res
    },

    boruta_tracking_info() {
      return (
        this.fetch_poid_tracking_info(
          'boruta_tracking_info',
          this.metavariables_tracking_info.boruta_poid
        ) || {}
      )
    },
  },
}

export { brick_generic_modelgenerator_tracking_info_mixin }

// ************************************************************************* //

// let brick_with_single_parent_mixin = {
//   computed: {
//     table_options() {
//       let options = [{ value: -1, text: this.$t('choose_a_table') }]
//       let self = this
//       _.forEach(this.tables_undeleted['lists'], function(x) {
//         // Prevent self referencing
//         if (x !== self.brick_id) {
//           options.push({
//             value: x,
//             text: self.bricks_undeleted['bricks'][x].name,
//           })
//         }
//       })
//       return options
//     },
//   },
// }

// export { brick_with_single_parent_mixin }

// ************************************************************************* //

let summary_mixin = {
  data() {
    return {
      poid_status_tokensub: _.uniqueId('poid_status'),
    }
  },
  computed: {
    bricks_undeleted() {
      return this.automl_bricks_undeleted_object_per_project(this.project_id)
    },
    cur_brick() {
      let result = this.bricks_undeleted.bricks[this.brick_id] || {}
      return result
    },
    is_valid_brick() {
      return !_.isEmpty(this.cur_brick)
    },

    ...mapGetters(['automl_bricks_undeleted_object_per_project']),
  },
  beforeMount() {
    this.fetch_collections(['bricks'])
  },
}

export { summary_mixin }

// ************************************************************************* //

let validation_buttons_bar_wrapper_mixin = {
  data() {
    return {
      is_validating: false,
    }
  },
  methods: {
    cancel() {
      this.$router.push(this.cancel_path)
    },
    async save() {
      await this.save_function()
      this.$refs.validation_buttons_bar.show_alert()
    },
    async validate() {
      this.is_validating = true
      await this.save()
      this.cancel()
    },
  },
}

export { validation_buttons_bar_wrapper_mixin }

// ************************************************************************* //

let global_brick_mixin = {
  mixins: [subscription_mixin, brick_mixin, metavariables_tracking_info_mixin],

  props: [
    'brick_id',
    'project_id',
    'description_name',
    'metavariables_dicts_poids',
  ],
}

export { global_brick_mixin }
